import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, CheckCircle2 } from "lucide-react";
import TopbarInner from "../components/topbar/TopbarInner";
import { useActivity } from "../state/ActivityProvider";

// Helper à ajouter en haut du fichier
function toISOParis(date = new Date()) {
  const f = new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const [dd, mm, yyyy] = f.format(date).split("/");
  return `${yyyy}-${mm}-${dd}`;
}

export default function RessentiPage({ user, onThemeToggle }) {
  const navigate = useNavigate();
  const { activity, addEntry, deleteEntry } = useActivity();

  const [text, setText] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
const [isSaving, setIsSaving] = useState(false);
const [errorMsg, setErrorMsg] = useState("");

  // Filtrer uniquement les ressentis
  const ressentis = activity
    .filter((e) => e.type === "ressenti")
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

const handleSave = async () => {
  if (!text.trim() || isSaving) return;

  setErrorMsg("");
  setIsSaving(true);

  try {
    const dateISO = toISOParis(new Date());

    await addEntry({
      dateISO,
      type: "ressenti",
      minutes: 0,
      content: text.trim(),
    });

    setText("");
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2500);
  } catch (error) {
    console.error("❌ Erreur sauvegarde ressenti :", error);
    setErrorMsg("Impossible de sauvegarder le ressenti. Vérifie la connexion et réessaie.");
  } finally {
    setIsSaving(false);
  }
};

const handleDelete = async (id) => {
  try {
    setErrorMsg("");
    await deleteEntry(id);
  } catch (error) {
    console.error("❌ Erreur suppression ressenti :", error);
    setErrorMsg("Impossible de supprimer ce ressenti pour le moment.");
  }
};

  // Formatter la date pour l'affichage
const formatDate = (dateISO, createdAt) => {
  if (!dateISO) return "";
  const date = createdAt ? new Date(createdAt) : (() => {
    const [y, m, d] = dateISO.split("-").map(Number);
    return new Date(y, m - 1, d);
  })();
  return date.toLocaleString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

  return (
    <>
      <TopbarInner
        title="Journal de ressenti"
        onBack={() => navigate(-1)}
        onThemeToggle={onThemeToggle}
        titleColor="#36495B"
      />

      <div className="ressenti-page app-safe--no-top">
        {/* FORM */}
        <div className="form-card">
          <div className="emoji">🪶</div>
          <h1>Exprimez votre ressenti</h1>
          <p>Notez vos sensations, pensées ou émotions après votre séance ✨</p>

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Comment vous sentez-vous aujourd’hui ?"
            rows="6"
          />

<button
  onClick={handleSave}
  disabled={!text.trim() || isSaving}
  className="save-btn"
>
  {isSaving ? "Sauvegarde..." : "💾 Sauvegarder mon ressenti"}
</button>

          {showSuccess && (
            <div className="success">
              <CheckCircle2 size={20} /> Sauvegardé avec succès !
            </div>
          )}
        </div>
{errorMsg && (
  <div className="error-box">
    {errorMsg}
  </div>
)}

        {/* HISTORIQUE */}
        {ressentis.length > 0 && (
          <div className="history-section">
            <h2>🕰️ Vos ressentis précédents</h2>
            {ressentis.map((entry) => (
              <div key={entry.id} className="history-item">
                <div className="top">
                  <span className="date">{formatDate(entry.dateISO, entry.createdAt)}</span>
                  <button
                    className="delete"
                    onClick={() => handleDelete(entry.id)}
                    title="Supprimer ce ressenti"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <p>{entry.content}</p>
              </div>
            ))}
          </div>
        )}

        {/* STYLE */}
        <style>{`
          .ressenti-page {
            min-height: 100vh;
            background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
            padding: 40px 20px 80px;
            margin-top: 55px; /* espace sous la TopbarInner */
            display: flex;
            flex-direction: column;
            align-items: center;
            font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
            animation: fadeIn 0.6s ease;
          }

          .form-card {
            background: white;
            border-radius: 20px;
            padding: 32px 28px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.08);
            max-width: 700px;
            width: 100%;
            text-align: center;
            transition: all 0.3s ease;
          }

          .form-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 28px rgba(99,102,241,0.08);
          }

          .emoji {
            font-size: 48px;
            margin-bottom: 8px;
          }

          h1 {
            font-size: 1.75rem;
            font-weight: 700;
            color: #1f2937;
            margin-bottom: 4px;
          }

          p {
            color: #6b7280;
            margin-bottom: 24px;
          }

          textarea {
            width: 100%;
            padding: 16px;
            border-radius: 14px;
            border: 2px solid #e5e7eb;
            font-size: 1rem;
            font-family: inherit;
            resize: vertical;
            min-height: 180px;
            line-height: 1.5;
            transition: all 0.25s ease;
            background: #f9fafb;
          }

          textarea:focus {
            outline: none;
            border-color: #164936;
            box-shadow: 0 0 0 4px rgba(99,102,241,0.15);
            background: white;
          }

          .save-btn {
            width: 100%;
            padding: 14px 20px;
            margin-top: 16px;
            border: none;
            border-radius: 12px;
            background: linear-gradient(135deg, #76A5A7, #61837A);
            color: white;
            font-weight: 600;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.25s ease;
          }

          .save-btn:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 6px 18px rgba(99,102,241,0.25);
          }

          .save-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .success {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: #ecfdf5;
            color: #047857;
            border: 1px solid #10b981;
            padding: 10px 16px;
            border-radius: 10px;
            margin-top: 16px;
            font-weight: 500;
            animation: fadeSlide 0.4s ease;
          }


.error-box {
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  font-weight: 500;
}
          .history-section {
            margin-top: 36px;
            width: 100%;
            max-width: 700px;
          }

          .history-section h2 {
            color: #1f2937;
            font-size: 1.25rem;
            font-weight: 600;
            margin-bottom: 16px;
          }

          .history-item {
            background: rgba(255,255,255,0.9);
            border: 1px solid #e5e7eb;
            border-radius: 14px;
            padding: 16px 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            margin-bottom: 12px;
            transition: all 0.25s ease;
          }

          .history-item:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(99,102,241,0.08);
          }

          .top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
          }

          .date {
            font-size: 0.875rem;
            color: #6b7280;
          }

          .delete {
            background: none;
            border: none;
            cursor: pointer;
            color: #9ca3af;
            transition: all 0.2s ease;
          }

          .delete:hover {
            color: #ef4444;
            transform: scale(1.1);
          }

          .history-item p {
            color: #374151;
            white-space: pre-wrap;
            text-align: left;
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }

          @keyframes fadeSlide {
            from { opacity: 0; transform: translateY(-6px); }
            to { opacity: 1; transform: translateY(0); }
          }

          @media (max-width: 767px) {
            .ressenti-page {
              padding: 32px 16px 60px;
              margin-top: 90px; /* topbar un peu plus compacte sur mobile */
            }

            .form-card {
              padding: 24px 20px;
            }
          }
        `}</style>
      </div>
    </>
  );
}